// Unpublished Work © 2023-2024 Deere & Company.

import { Theme } from "@deere/fuel-react";
import { Box, List, ListItem, Typography } from "@mui/material";
import {
  GridCellParams,
  gridClasses,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid-pro";
import { TFunction } from "i18next";
import ResourceLink from "../../../../components/ResourceLink/ResourceLink";
import { LineStatus, OrderLineItem, OrderStatus } from "../../../../types";
import { createJDPointOrderLink } from "../../../../utils";

export const getTableStyles = (theme: Theme) => ({
  border: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  [`.${gridClasses.overlayWrapper}`]: {
    height: "50vh",
    borderRadius: "10px",
  },
  [`.${gridClasses.overlayWrapperInner}`]: {
    height: "100% !important",
  },
  [`.${gridClasses.virtualScroller}`]: {
    overflow: "hidden",
    border: 0,
    overflowX: "scroll",
  },
  [`.${gridClasses.cell}`]: {
    borderTop: "none",
    outline: "none !important",
    display: "flex",
    paddingBottom: "8px",
    alignItems: "center",
    paddingTop: "16px",
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
    "& a svg": {
      marginTop: "auto",
      marginBottom: "auto",
    },
  },
  [`.MuiDataGrid-cell.MuiDataGrid-cell--editing`]: {
    boxShadow: "none !important",
    padding: "10px",
    outline: "none !important",
    backgroundColor: "transparent !important",
  },
  [`.MuiDataGrid-columnHeader`]: {
    width: "3rem",
  },
  [`.MuiDataGrid-columnHeaderTitleContainer`]: {
    whiteSpace: "no-wrap",
  },
  [`.MuiDataGrid-columnHeaderTitle`]: {
    whiteSpace: "no-wrap",
    overflow: "unset",
  },
  [`.MuiDataGrid-withBorderColor`]: {
    borderColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  [`& .${gridClasses.columnHeader}`]: {
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
    "&:hover": {
      backgroundColor: theme.tokens.FuelColorGray50,
    },
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  },
  [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
    backgroundColor: theme.tokens.FuelColorBackgroundWhite,
  },
  [`& .${gridClasses.columnHeaders}`]: {
    borderTop: 1,
    borderColor: theme.tokens.FuelColorBorderLight,
  },
  [`.${gridClasses.columnSeparator}`]: {
    color: theme.tokens.FuelColorBorderLight,
  },
  [`.MuiCheckbox-root.Mui-disabled`]: {
    visibility: "hidden",
  },
  [`.${gridClasses.checkboxInput}`]: {
    color: theme.tokens.FuelColorGray400,
  },
  [`.MuiCheckbox-root.Mui-checked`]: {
    color: theme.tokens.FuelColorBrandPrimary,
  },
  [`& .${gridClasses.toolbarContainer} > button`]: {
    border: "none",
    color: theme.tokens.FuelColorBrandPrimary,
    boxShadow: "none",
  },
  [`.line-item-child`]: {
    borderBottom: "transparent",
    borderTop: "transparent",
  },
  [`.line-item-child-first-child`]: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "45px",
      width: "calc(100% - 45px)",
      borderTop: `1px solid ${theme.tokens.FuelColorBorderLight} !important`,
    },
  },
  [`.line-item-parent`]: {
    borderTop: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  },
  [`& .${gridClasses.row} > div`]: {
    alignItems: "flex-start",
  },
  [`& .MuiDataGrid-row--borderBottom`]: {
    borderBottom: `solid ${theme.tokens.FuelColorBorderLight} 1px`,
  },
  [`& .${gridClasses.columnSeparator}`]: {
    color: "transparent",
    transition: "color 0.2s",
  },
  [`& .${gridClasses.columnHeaders}:hover .${gridClasses.columnSeparator}`]: {
    color: theme.tokens.FuelColorBorderLight,
  },
});

export const generateColumnHeaderStyles = (theme: Theme) => ({
  fontWeight: theme.tokens.FuelFontWeightSemibold,
  fontSize: theme.tokens.FuelFontSizeHeading6,
  textOverflow: "ellipsis",
  overflow: "hidden !important",
});

export const generateRowStyles = (theme: Theme) => ({
  fontSize: theme.tokens.FuelFontSizeSm,
  textOverflow: "ellipsis",
  overflow: "hidden !important",
});

export const getMessages = (
  investigationStatusCodes: string[] = [],
  t: TFunction
): string[] =>
  investigationStatusCodes.map((code) =>
    t(`orders:orderLines.warning.description.${code}`)
  );

export const addCommonColDefinitions = (column: GridColDef): GridColDef =>
  ({
    ...column,
    cellClassName: (params: GridCellParams) => {
      const { orderObject } = params.row;
      const singleSubLine =
        orderObject.lineType === "scheduledLines" &&
        orderObject.lineNumber.length === 1;

      if (orderObject.lineType === "parentLineItem" || singleSubLine)
        return "parentCell";
      return "";
    },
  } as GridColDef);

export function getInvestigationTooltip(
  params: GridRenderCellParams<any, any, any>,
  investigationTitle: string,
  theme: Theme,
  t: TFunction
) {
  const investigationMessages = getMessages(
    params.row.orderObject.statusCodes,
    t
  );
  const messagesElements = investigationMessages.map((message) => (
    <ListItem
      sx={{
        display: "list-item",
        paddingBottom: 0,
        paddingTop: 0,
        fontSize: theme.tokens.FuelFontSizeHeading6,
        fontWeight: "400",
        lineHeight: "16px",
      }}
      key={message}
    >
      {message}
    </ListItem>
  ));
  const finalMessageElement = (
    <>
      <Typography
        sx={{
          padding: 0,
          paddingBottom: 0,
          paddingTop: 0,
          fontSize: theme.tokens.FuelFontSizeHeading6,
          fontWeight: "400",
          lineHeight: "16px",
        }}
      >
        {messagesElements.length > 0
          ? `${investigationTitle}:`
          : investigationTitle}
      </Typography>
      <List
        style={{
          listStyleType: "disc",
          listStylePosition: "inside",
          paddingBottom: 0,
          paddingTop: 0,
        }}
      >
        {messagesElements}
      </List>
    </>
  );

  return finalMessageElement;
}

export function getCancellationMessages(codes: string[], t: TFunction) {
  let finalMessages = getMessages(codes, t);
  if (finalMessages.length === 0) {
    finalMessages = [t("orders:orderLines.warning.description.CANCELLED")];
  }
  return finalMessages;
}

export const getWarehouseCancellationForJDPointOrderTooltip = (
  externalOrderNumber: string | undefined,
  accountId: string,
  JDPOINT_URL: string
) => (
  <Box>
    {externalOrderNumber && (
      <ResourceLink
        value={externalOrderNumber}
        url={createJDPointOrderLink(
          JDPOINT_URL,
          externalOrderNumber,
          btoa(accountId as string)
        )}
      />
    )}
  </Box>
);

export const getParentLineCount = (orderLineItems: OrderLineItem[]) =>
  orderLineItems.filter((orderLineItem) => {
    if (!orderLineItem) return false;

    const parentLine = orderLineItem.lineType === "parentLineItem";
    const singleSubLine =
      orderLineItem.lineType === "scheduledLines" &&
      orderLineItem.lineNumber.length === 1;

    return parentLine || singleSubLine;
  }).length;

export const orderNumberStartingWith9Series = (orderNumber: String) =>
  orderNumber.startsWith("9");

const D2D_LINE_STATUSES: string[] = [
  LineStatus.ROUTED_TO_DEALER,
  LineStatus.SENT_TO_DEALER,
];

export const isD2DLineStatus = (status: string) =>
  D2D_LINE_STATUSES.includes(status);

export const isCancellationVisibleInRows = (
  row: any,
  orderStatus: OrderStatus
) =>
  !row.orderObject.status?.includes("CANCELLED") &&
  !row.orderObject.status?.includes("SHIPPED") &&
  !row.orderObject.status?.includes("IN_PROCESS") &&
  !isD2DLineStatus(row.orderObject.status) &&
  !isOrderedParentLineItem(row) &&
  !row.orderObject.vendorDirectPart &&
  (!row.orderObject.cancellationDetail ||
    !row.orderObject.cancellationDetail.isRequestedForCancellation) &&
  orderStatus !== OrderStatus.SUBMITTED &&
  !isSendToFactoryStatus(row.orderObject.status);

export const isCancellationVisible = (
  params: GridRenderCellParams<any, any, any> | GridRowParams<any>,
  orderStatus: OrderStatus
) => isCancellationVisibleInRows(params.row, orderStatus);

const isOrderedParentLineItem = (row: any) =>
  row.orderObject.status?.includes("ORDERED") &&
  row.orderObject.lineType === "parentLineItem";

export const isSendToFactoryStatus = (status: string) =>
  status === LineStatus.SENT_TO_FACTORY;
